import { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Box,
  Button,
  Stack,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Skeleton,
  Select,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../redux/userSlice';
import { reset, loadSession, startNewSession } from '../redux/questionSlice';
import { clearAutoLogout } from '../utils/authUtils';
import { getSessions } from '../services/astroService';
import { getMatches } from '../services/compatService';
import { formatShortDate, getCountry, setCountryOverride } from '../utils/localeUtils';
import logo from '../assets/images/sitaare_logo.png';

const COUNTRY_OPTIONS = [
  { code: 'IN', label: '🇮🇳 India' },
  { code: 'US', label: '🇺🇸 US' },
];

const DRAWER_WIDTH = 300;

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return formatShortDate(dateStr);
};

const groupSessionsByDate = (sessions) => {
  const groups = {};
  sessions.forEach((s) => {
    const label = formatDate(s.lastActivity);
    if (!groups[label]) groups[label] = [];
    groups[label].push(s);
  });
  return groups;
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const currentSessionId = useSelector((s) => s.question.session.id);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [country, setCountry] = useState(
    (typeof window !== 'undefined' && localStorage.getItem('countryOverride')) || getCountry() || 'US'
  );

  const handleCountryChange = (e) => {
    const val = e.target.value;
    setCountry(val);
    setCountryOverride(val);
  };

  const isHomePage = location.pathname === '/';
  const isSignInPage = location.pathname === '/signin';
  const isSignupPage = location.pathname === '/signup';
  const isChatPage = location.pathname === '/chat';

  const fetchSessions = useCallback(async () => {
    if (!user.isLoggedIn) return;
    setSessionsLoading(true);
    try {
      const data = await getSessions();
      setSessions(data || []);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, [user.isLoggedIn]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
    if (user.isLoggedIn) {
      fetchSessions();
      // Matches are an active-account feature; the endpoint 403s for temp users (→ []).
      if (user.status !== 'temp') {
        getMatches().then(setMatches).catch(() => setMatches([]));
      }
    }
  };

  const handleNewMatch = () => {
    navigate('/match');
    handleDrawerClose();
  };

  const handleMatchClick = (matchId) => {
    navigate(`/match/${matchId}`);
    handleDrawerClose();
  };

  const handleDrawerClose = () => setDrawerOpen(false);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(reset());
    localStorage.removeItem('token');
    clearAutoLogout();
    handleDrawerClose();
    navigate('/');
  };

  const handleProfile = () => {
    handleDrawerClose();
    navigate('/profile');
  };

  const handleHelp = () => {
    handleDrawerClose();
    navigate('/help');
  };

  const handleSessionClick = (sessionId) => {
    if (sessionId !== currentSessionId) {
      dispatch(loadSession(sessionId));
    }
    if (!isChatPage) navigate('/chat');
    handleDrawerClose();
  };

  const handleNewChat = () => {
    dispatch(startNewSession());
    if (!isChatPage) navigate('/chat');
    handleDrawerClose();
  };

  const stringToColor = (string) => {
    let hash = 0;
    let i;
    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */
    return color;
  };

  const stringAvatar = (name) => {
    const parts = name.trim().split(' ');
    const initials =
      parts.length === 1
        ? parts[0][0]
        : parts[0][0] + parts[1][0];
    return {
      sx: {
        bgcolor: stringToColor(name),
        width: { xs: 32, sm: 40 },
        height: { xs: 32, sm: 40 },
        fontSize: { xs: '0.875rem', sm: '1rem' },
      },
      children: initials.toUpperCase(),
    };
  };

  const grouped = groupSessionsByDate(sessions);

  return (
    <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: '#ffffff' }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.75, sm: 1 },
          minHeight: { xs: '48px', sm: '64px' }
        }}
      >
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Box
            component="img"
            src={logo}
            alt="Sitaare Logo"
            sx={{
              width: { xs: '80px', sm: '120px' },
              cursor: 'pointer',
              display: isHomePage ? 'none' : 'block',
            }}
          />
        </Link>
        <Box sx={{ display: isSignInPage || isSignupPage ? 'none' : 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {isHomePage && !user.isLoggedIn && (
            <Button
              component={Link}
              to="/learn"
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                color: 'text.secondary',
                fontWeight: 500,
                py: 0.5,
                px: 1,
                minWidth: 0,
                '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
              }}
            >
              Vedic Guide
            </Button>
          )}
          {isHomePage && !user.isLoggedIn && (
            <Button
              component={Link}
              to="/blogs"
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                color: 'text.secondary',
                fontWeight: 500,
                py: 0.5,
                px: 1,
                minWidth: 0,
                '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
              }}
            >
              Blog
            </Button>
          )}
          {user.isLoggedIn && <Select
            value={country}
            onChange={handleCountryChange}
            size="small"
            variant="standard"
            disableUnderline
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              '& .MuiSelect-select': { py: 0.5, px: 0.75 },
            }}
          >
            {COUNTRY_OPTIONS.map((opt) => (
              <MenuItem key={opt.code} value={opt.code} sx={{ fontSize: '0.85rem' }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>}
          {user.isLoggedIn ? (
            <IconButton
              onClick={handleDrawerOpen}
              size="small"
              sx={{
                p: { xs: 0.5, sm: 1 },
                borderRadius: '12px',
                gap: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <MenuIcon sx={{ fontSize: { xs: 18, sm: 22 }, color: 'text.secondary' }} />
              <Avatar {...stringAvatar(user.name)} />
            </IconButton>
          ) : isHomePage ? (
            <Button
              component={Link}
              to="/signin"
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                color: 'text.secondary',
                fontWeight: 500,
                py: 0.5,
                px: 1,
                minWidth: 0,
                '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
              }}
            >
              Sign in
            </Button>
          ) : null}

          {/* Right-side profile + history drawer */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleDrawerClose}
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Header with close */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, flexShrink: 0 }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{user.name}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{user.email}</Typography>
                </Box>
                <IconButton size="small" onClick={handleDrawerClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Divider />

              {/* Profile — hidden for temp (anonymous trial) users until they sign up. */}
              <List disablePadding>
                {user.status !== 'temp' && (
                  <ListItemButton onClick={handleProfile} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>
                      Profile
                    </ListItemText>
                  </ListItemButton>
                )}
                <ListItemButton onClick={handleHelp} sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <HelpOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>
                    Help
                  </ListItemText>
                </ListItemButton>
              </List>

              {/* Chat History — only on /chat */}
              {user.isLoggedIn && (
                <>
                  <Divider />

                  {/* New Chat + New Match buttons */}
                  <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        startIcon={<RateReviewOutlinedIcon sx={{ fontSize: '18px !important' }} />}
                        onClick={handleNewChat}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: 2 }}
                      >
                        New Chat
                      </Button>
                      {user.status !== 'temp' && (
                        <Button
                          size="small"
                          fullWidth
                          variant="outlined"
                          startIcon={<FavoriteBorderIcon sx={{ fontSize: '18px !important' }} />}
                          onClick={handleNewMatch}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: 2 }}
                        >
                          New Match
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  {/* Match history (completed only) */}
                  {matches.filter((m) => m.status === 'complete').length > 0 && (
                    <Box sx={{ px: 1, pt: 0.5 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ px: 1.5, py: 0.5, display: 'block', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}
                      >
                        Matches
                      </Typography>
                      <List dense disablePadding>
                        {matches.filter((m) => m.status === 'complete').slice(0, 6).map((m) => {
                          const other = m.isInitiator ? (m.partnerName || m.inviteEmail) : m.initiatorName;
                          return (
                            <ListItemButton
                              key={m.matchId}
                              onClick={() => handleMatchClick(m.matchId)}
                              sx={{ borderRadius: 1.5, py: 0.6, px: 1.5, mb: 0.25 }}
                            >
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <FavoriteBorderIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={other || 'Match'}
                                secondary={m.score ? `${m.score.total}/36` : undefined}
                                primaryTypographyProps={{ fontSize: '0.8rem', noWrap: true, sx: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                secondaryTypographyProps={{ fontSize: '0.7rem' }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                      <Divider sx={{ mt: 0.5 }} />
                    </Box>
                  )}

                  {/* Chat list */}
                  <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 0.5 }}>
                    {sessionsLoading ? (
                      <Box sx={{ px: 1 }}>
                        {[...Array(5)].map((_, i) => (
                          <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />
                        ))}
                      </Box>
                    ) : sessions.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 2, display: 'block' }}>
                        No previous chats
                      </Typography>
                    ) : (
                      Object.entries(grouped).map(([label, group]) => (
                        <Box key={label} sx={{ mb: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              display: 'block',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}
                          >
                            {label}
                          </Typography>
                          <List dense disablePadding>
                            {group.map((s) => (
                              <ListItemButton
                                key={s._id}
                                selected={s._id === currentSessionId}
                                onClick={() => handleSessionClick(s._id)}
                                sx={{
                                  borderRadius: 1.5,
                                  py: 0.6,
                                  px: 1.5,
                                  mb: 0.25,
                                  '&.Mui-selected': {
                                    bgcolor: 'action.selected',
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={s.title}
                                  primaryTypographyProps={{
                                    fontSize: '0.8rem',
                                    noWrap: true,
                                    sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                                  }}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </Box>
                      ))
                    )}
                  </Box>
                </>
              )}

              {/* Spacer */}
              {!user.isLoggedIn && <Box sx={{ flex: 1 }} />}

              {/* Logout — hidden for temp users: they have no password/email to sign back in with,
                  so logout is effectively account abandonment. */}
              {user.status !== 'temp' && (
                <>
                  <Divider />
                  <List disablePadding>
                    <ListItemButton onClick={handleLogout} sx={{ py: 1.5, px: 2, color: 'error.main' }}>
                      <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>
                        Logout
                      </ListItemText>
                    </ListItemButton>
                  </List>
                </>
              )}
            </Box>
          </Drawer>
        </Box>
      </Stack>
    </AppBar>
  );
};

export default Header;
